import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { ScreenHead } from '../../ui/screen-parts.js';
import { Chip, Panel } from '../../ui/kit.js';
import { CAMERAS } from '../../data/cameras.js';
import { CameraTile } from '../../ui/cameras/camera-tile.js';
import { CameraViewer } from '../../ui/cameras/camera-viewer.js';
import { useFullscreenSession } from '../../ui/cameras/fullscreen.js';
import { LockIcon } from '../../ui/cameras/icons.js';
import { ago, useNow } from '../../ui/cameras/media.js';

const EASE = [0.16, 1, 0.3, 1];
const DRIFT_MS = 10000;

/* What each amenity holds and when it fills up. A camera marked for
   residents that is not listed here still shows, with a generic room. */
const AMENITIES = {
    lobby:          { label: 'Lobby',        capacity: 30, typical: 0.3,  hours: 'Open 24 hours',        peak: 'Busiest 5 to 7 PM' },
    'garden-lobby': { label: 'Garden lobby', capacity: 20, typical: 0.2,  hours: 'Open 24 hours',        peak: 'Busiest 8 to 9 AM' },
    'spiral-stair': { label: 'Spiral stair', capacity: 15, typical: 0.2,  hours: 'Open 24 hours',        peak: 'Busiest 8 to 9 AM' },
    cafe:           { label: 'Café',         capacity: 24, typical: 0.45, hours: 'Open 7 AM to 3 PM',    peak: 'Busiest 8 to 10 AM' },
    laundry:        { label: 'Laundry',      capacity: 8,  typical: 0.3,  hours: 'Open 6 AM to 11 PM',   peak: 'Busiest on Sundays' },
    pool:           { label: 'Pool deck',    capacity: 40, typical: 0.5,  hours: 'Open 8 AM to 9 PM',    peak: 'Busiest 1 to 4 PM' },
    'lap-pool':     { label: 'Lap pool',     capacity: 12, typical: 0.3,  hours: 'Open 5 AM to 10 PM',   peak: 'Busiest 6 to 8 AM' },
    lounge:         { label: 'Co-working',   capacity: 24, typical: 0.35, hours: 'Open 6 AM to 11 PM',   peak: 'Busiest 10 AM to 3 PM' },
    conference:     { label: 'Conference',   capacity: 14, typical: 0.3,  hours: 'Book in the app',      peak: 'Busiest 9 AM to noon' },
    library:        { label: 'Library',      capacity: 16, typical: 0.25, hours: 'Open 7 AM to 11 PM',   peak: 'Busiest after 7 PM' },
    bar:            { label: 'Lounge bar',   capacity: 20, typical: 0.3,  hours: 'Open 4 PM to 11 PM',   peak: 'Busiest 7 to 9 PM' },
    rooftop:        { label: 'Rooftop',      capacity: 30, typical: 0.35, hours: 'Open 10 AM to 11 PM',  peak: 'Busiest at sunset' }
};

const amenityOf = camera => AMENITIES[camera.key] || { label: camera.name, capacity: 20, typical: 0.3, hours: '', peak: '' };

function levelOf(count, capacity) {
    const share = count / capacity;
    if (share < 0.34) return { word: 'Quiet', tone: 'ok' };
    if (share < 0.67) return { word: 'Moderate', tone: 'warn' };
    return { word: 'Busy', tone: 'alert' };
}

/* A believable start: around each room's usual level, not all equal. */
function seedCounts(cameras) {
    return Object.fromEntries(cameras.map(camera => {
        const a = amenityOf(camera);
        const base = a.capacity * a.typical;
        return [camera.id, Math.max(0, Math.round(base + (Math.random() - 0.5) * base * 0.6))];
    }));
}

/* People come and go: a step of up to two, pulled gently back toward
   the room's usual level so a count never wanders off to empty or full. */
function drift(count, amenity) {
    const target = amenity.capacity * amenity.typical;
    const pull = (target - count) * 0.15;
    const step = Math.round(pull + (Math.random() * 4 - 2));
    return Math.min(amenity.capacity, Math.max(0, count + step));
}

/**
 * Live Cams, the residents' half of the camera system: the shared
 * amenities only, framed around the question a resident actually has,
 * which is whether to go now.
 *
 * Each card is the live picture with a head count that moves every few
 * seconds and a meter against the room's capacity. Tapping a card opens
 * the same full-screen viewer the security desk uses, without the
 * desk's tools: no PTZ, snapshots, playback or talk-down.
 */
function LiveCamsScreen() {
    const reduced = useReducedMotion();
    const cameras = useMemo(() => CAMERAS.filter(c => c.resident), []);
    const [counts, setCounts] = useState(() => seedCounts(cameras));
    const [updated, setUpdated] = useState(() => Date.now());
    const [openId, setOpenId] = useState(null);

    useEffect(() => {
        const timer = setInterval(() => {
            setCounts(current => Object.fromEntries(cameras.map(camera => (
                [camera.id, drift(current[camera.id] ?? 0, amenityOf(camera))]
            ))));
            setUpdated(Date.now());
        }, DRIFT_MS);
        return () => clearInterval(timer);
    }, [cameras]);

    const fullscreen = useFullscreenSession(!!openId, () => setOpenId(null));

    const open = id => {
        fullscreen.enter();
        setOpenId(id);
    };

    const close = () => {
        setOpenId(null);
        fullscreen.leave();
    };

    /* The quietest room, as a suggestion, from the rooms that are open
       to drop into (the lobby is a thoroughfare, not a destination). */
    const quietest = cameras
        .filter(c => c.key !== 'lobby')
        .map(c => ({ camera: c, amenity: amenityOf(c), share: (counts[c.id] ?? 0) / amenityOf(c).capacity }))
        .sort((a, b) => a.share - b.share)[0];

    return (
        <div className="screen-content livecams">
            <ScreenHead
                eyebrow="Live cams"
                title="See how busy it is before you go"
                aside={<Chip tone="info">{cameras.length} amenities live</Chip>}
            />

            {quietest && (
                <Panel className="livecams__tip panel--accent">
                    <span className="livecams__tip-dot" aria-hidden="true" />
                    <p>
                        <strong>Right now, the {quietest.amenity.label.toLowerCase()} is the quietest.</strong>{' '}
                        {counts[quietest.camera.id] === 0
                            ? 'Nobody is there.'
                            : `${counts[quietest.camera.id]} ${counts[quietest.camera.id] === 1 ? 'person' : 'people'} inside.`}
                    </p>
                </Panel>
            )}

            <div className="livecams__grid">
                {cameras.map((camera, i) => {
                    const amenity = amenityOf(camera);
                    const count = counts[camera.id] ?? 0;
                    const level = levelOf(count, amenity.capacity);
                    const pct = Math.round((count / amenity.capacity) * 100);

                    return (
                        <Panel as="article" className="livecam" key={camera.id} index={i}>
                            <CameraTile
                                camera={camera}
                                index={i}
                                variant="soft"
                                onExpand={open}
                                label={`${amenity.label}, live. Open full screen`}
                                active={!openId}
                            />
                            <button type="button" className="livecam__body" onClick={() => open(camera.id)}>
                                <span className="livecam__row">
                                    <span className="livecam__name">
                                        <strong>{amenity.label}</strong>
                                        <span>{amenity.hours || camera.location}</span>
                                    </span>
                                    <Chip tone={level.tone}>{level.word}</Chip>
                                </span>
                                <span className="livecam__count">
                                    <Count value={count} reduced={reduced} />
                                    <span className="livecam__people">
                                        {count === 1 ? 'person' : 'people'}, {level.word.toLowerCase()}
                                    </span>
                                </span>
                                <span
                                    className={`meter livecam__meter livecam__meter--${level.tone}`}
                                    role="meter"
                                    aria-valuemin={0}
                                    aria-valuemax={amenity.capacity}
                                    aria-valuenow={count}
                                    aria-label={`${amenity.label}: ${count} of ${amenity.capacity}`}
                                >
                                    <span className="meter__fill" style={{ width: `${Math.max(pct, 3)}%` }} />
                                </span>
                                <span className="livecam__foot">
                                    <span>{pct}% of capacity</span>
                                    {amenity.peak && <span>{amenity.peak}</span>}
                                </span>
                            </button>
                        </Panel>
                    );
                })}
            </div>

            <p className="livecams__privacy">
                <LockIcon aria-hidden="true" />
                <span>
                    Shared amenities only, with no audio. Corridors, elevators and private floors are never
                    shown here, and what you watch is not saved. <Updated t={updated} />
                </span>
            </p>

            <CameraViewer
                cameras={cameras}
                cameraId={openId}
                onNavigate={setOpenId}
                onClose={close}
                eyebrow="Live cams"
                variant="soft"
                info={camera => {
                    const amenity = amenityOf(camera);
                    const count = counts[camera.id] ?? 0;
                    const level = levelOf(count, amenity.capacity);
                    return (
                        <span className="livecam__viewer-info">
                            <Chip tone={level.tone}>{level.word}</Chip>
                            <span>{amenity.label}: {count} {count === 1 ? 'person' : 'people'}</span>
                        </span>
                    );
                }}
            />
        </div>
    );
}

/* The head count, rolling to its new value rather than snapping. */
function Count({ value, reduced }) {
    return (
        <span className="livecam__num">
            <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                    key={value}
                    initial={reduced ? { opacity: 0 } : { opacity: 0, y: '0.6em' }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={reduced ? { opacity: 0 } : { opacity: 0, y: '-0.6em' }}
                    transition={{ duration: 0.4, ease: EASE }}
                >
                    {value}
                </motion.span>
            </AnimatePresence>
        </span>
    );
}

function Updated({ t }) {
    const now = useNow();
    return <span className="livecams__updated">Counts updated {ago(t, now).toLowerCase()}.</span>;
}

export default LiveCamsScreen;
